import React from 'react';
import _ from 'underscore';

import {ANTIBODY_SCHEMA_INTERFACE_ID} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {Capability} from 'auth/capabilities/CapabilityFeatureConstants';
import {isCapabilityEnabledForListing} from 'auth/capabilities/isCapabilityEnabledForListing';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants, {PROCESS_TYPES} from 'constants/ModelTypeConstants';
import {
  getRegistrySupportedOligoAwareTypes,
  RegistryFilterOption,
  SchemaInterfaceExtendingSchema,
  TagSchema,
  TagSchemaSummary,
} from 'constants/RegistryConstants';
import {EQUIPMENT_SCHEMA_INTERFACE_ID} from 'instruments/equipment/EquipmentConstants';
import {ComplexPolymerInterfaceApiId} from 'modbio/complex-polymers/ComplexPolymerSchemaInterfaceConstants';
import {getSchemaSearchableMenuOptionDatas} from 'samples/SchemaStarsUtil';
import {SchemaInterfaceSummary} from 'schemas/schema-interface/SchemaInterfaceConstants';
import useFilterArchivedDropdownOptions from 'search/filters/base/useFilterArchivedDropdownOptions';
import * as FilterUtil from 'search/FilterUtil';
import {REACTION_PSEUDO_TYPE} from 'search/ReactionSearchConstants';
import {
  getTagSchemaOligoAwareFolderItemType,
  tagSchemaToAllOligoAwareFolderItemTypes,
} from 'tag-schema/util/TagSchemaUtil';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu, {SearchableMenuArchivableOptionDatas} from 'ui-library/menus/SearchableMenu';
import APIUtil from 'util/APIUtil';
import {getRenderStarredOptionContent} from 'util/getRenderStarredOptionContent';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';
import {capwords, typeToName} from 'util/NameUtil';

import {Filter, FilterKey, keyToEmptyFilter, MultiValueFilter} from '../SearchConstants';
import {EnhancedSchemaProps, ToggleSchemaStarFunctions} from './TypesAndSchemaFilterForm';
import {TypesMenuSchema} from './TypesAndSchemaFilterTypes';

const TYPES_WITH_STARRABLE_SCHEMAS = [
  ...getRegistrySupportedOligoAwareTypes(),
  ModelTypeConstants.ENTRY,
  ModelTypeConstants.BATCH,
  ModelTypeConstants.CONTAINER,
  ModelTypeConstants.BOX,
  ModelTypeConstants.PLATE,
  ModelTypeConstants.LOCATION,
];
interface TypesAndSchemaMenuProps extends EnhancedSchemaProps {
  filters: Array<Filter>;
  onChangeFilters: (filters: Array<Filter>) => void;
  schemasByAvailableType: {[type: string]: Array<TypesMenuSchema>};
  filteredInterfacesByAvailableTypes?: Array<SchemaInterfaceExtendingSchema>;
  isTypeRequired?: boolean;
  alwaysApplySchemasFilter?: boolean;
}

interface TypesAndSchemaValue {
  types: Array<string>;
  schemaId: string | null;
}

interface TypesAndInterfaceValue {
  types: Array<string>;
  interfaceId: Array<string> | null;
}

function getTypesValue(types: Array<string>): TypesAndSchemaValue {
  return {types, schemaId: null};
}

function getSchemaValue(
  types: Array<string>,
  schema: TypesMenuSchema | RegistryFilterOption
): TypesAndSchemaValue {
  return {
    types,
    schemaId: schema.id,
  };
}

export function getInterfaceValue(
  types: Array<string>,
  schemaInterface: SchemaInterfaceSummary
): TypesAndInterfaceValue {
  return {
    types,
    interfaceId: [schemaInterface.id],
  };
}

function getTagSchemaValue(tagSchema: TagSchemaSummary): TypesAndSchemaValue {
  return {
    types: tagSchemaToAllOligoAwareFolderItemTypes(tagSchema),
    schemaId: tagSchema.id,
  };
}

export function getAvailableProcessTypes(schemasByAvailableType: {
  [type: string]: Array<TypesMenuSchema>;
}): Array<string> {
  return PROCESS_TYPES.filter(
    (type) => schemasByAvailableType.hasOwnProperty(type) && isModelTypeEnabledForListing(type)
  );
}

function getBioentityTypeOptionDatas(types: Array<string>): Array<Array<OptionData>> {
  return [
    [
      {
        name: 'All',
        value: getTypesValue(types),
        extra: {unstarrable: true},
      },
    ],
    types
      .filter((type) => !isSearchableExclusiveSSIType(type))
      .map((type) => ({
        name: capwords(typeToName(type)),
        value: getTypesValue([type]),
        extra: {unstarrable: true},
      })),
  ];
}

function isSearchableExclusiveSSIType(type: string): boolean {
  if (type === ModelTypeConstants._DO_NOT_USE_ANTIBODY) {
    return true;
  }
  if (type === ModelTypeConstants._DO_NOT_USE_OLIGO_CONJUGATE) {
    return true;
  }
  if (type === ModelTypeConstants._DO_NOT_USE_OLIGO_DUPLEX) {
    return true;
  }
  if (type === ModelTypeConstants.EQUIPMENT) {
    return true;
  }
  return false;
}

function getSearchExclusiveSSIInterface(type: string): TypesAndInterfaceValue {
  if (type === ModelTypeConstants._DO_NOT_USE_ANTIBODY) {
    return getInterfaceValue([type], {id: ANTIBODY_SCHEMA_INTERFACE_ID, name: 'Protein'});
  }
  if (type === ModelTypeConstants._DO_NOT_USE_OLIGO_CONJUGATE) {
    return getInterfaceValue([type], {
      id: ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID,
      name: 'Oligo Conjugate',
    });
  }
  if (type === ModelTypeConstants._DO_NOT_USE_OLIGO_DUPLEX) {
    return getInterfaceValue([type], {
      id: ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID,
      name: 'Oligo Duplex',
    });
  }
  if (type === ModelTypeConstants.EQUIPMENT) {
    return getInterfaceValue([type], {
      id: EQUIPMENT_SCHEMA_INTERFACE_ID,
      name: 'Equipment',
    });
  }

  throw new Error(`Unsupported exclusive SSI type: ${type}`);
}

function _handleSelect(
  option: TypesAndSchemaValue | TypesAndInterfaceValue,
  filters: Array<Filter>,
  onChangeFilters: (filters: Array<Filter>) => void,
  schemasByAvailableType: {[type: string]: Array<TypesMenuSchema>},
  alwaysApplySchemasFilter?: boolean
): void {
  const filtersFromSelection: Array<Filter> = [
    {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: [
        ...new Set(
          option.types.map((t) =>
            isSearchableExclusiveSSIType(t) ? ModelTypeConstants.BASIC_FOLDER_ITEM : t
          )
        ),
      ],
    },
  ];

  // If only a type is selected and we want to apply a schemas filter, add a schemas filter for all available
  // schemas for each type
  if ((option as TypesAndSchemaValue).schemaId == null && alwaysApplySchemasFilter) {
    const schemaIdValues = [];
    for (const type of option.types) {
      schemaIdValues.push(schemasByAvailableType[type].map((schema) => schema.id));
    }
    filtersFromSelection.push({
      ...keyToEmptyFilter(FilterKey.SCHEMA),
      value: schemaIdValues.flat(),
    });
  }
  if ((option as TypesAndSchemaValue).schemaId != null) {
    filtersFromSelection.push({
      ...keyToEmptyFilter(FilterKey.SCHEMA),
      value: [(option as TypesAndSchemaValue).schemaId!],
    });
  } else if ((option as TypesAndInterfaceValue).interfaceId != null) {
    filtersFromSelection.push({
      ...keyToEmptyFilter(FilterKey.SCHEMA_INTERFACE),
      value: (option as TypesAndInterfaceValue).interfaceId,
    } as MultiValueFilter);
  }
  const newFilters = [
    // Remove filters first
    ...filters.filter(
      (filter) => ![FilterKey.TYPES, FilterKey.SCHEMA, FilterKey.SCHEMA_INTERFACE].includes(filter.key)
    ),
    // Add filters
    ...filtersFromSelection,
  ];
  onChangeFilters(newFilters);
}

/*
 * @param schemaType - the model type associated with the `schemas` (e.g. ENTRY)
 * @param types - available model types that can use these schemas (e.g. [ENTRY, STAGE_ENTRY])
 * @param schemas
 */
function _getTypeSubmenu(
  schemaType: string,
  types: Array<string>,
  schemas: Array<TypesMenuSchema>,
  starredSchemaIds: Array<string> | null
): Array<Array<OptionData>> | null {
  return schemas.length
    ? [
        getSchemaSearchableMenuOptionDatas(schemas, starredSchemaIds, {
          allSchemasValue: getTypesValue(types),
          transformSchema: (schema) => getSchemaValue(types, schema),
          unstarrable: !(TYPES_WITH_STARRABLE_SCHEMAS as Array<string>).includes(schemaType),
          includeArchivedSuffix: false,
        }),
      ]
    : null;
}

function _getTypeOption(
  schemaType: string,
  schemasByAvailableType: {[type: string]: Array<TypesMenuSchema>},
  starredSchemaIds: Array<string> | null,
  {
    hasSchemasDisabled,
    name,
    extraTypes = [],
  }: {hasSchemasDisabled?: boolean; name?: string; extraTypes?: Array<string>} = {}
): OptionData | null {
  // eslint-disable-next-line benchlint/no-in-operator
  if (!(schemaType in schemasByAvailableType)) {
    return null;
  }
  const types = [schemaType, ...extraTypes].filter((type) => isModelTypeEnabledForListing(type));
  if (types.length === 0) {
    return null;
  }

  return {
    name: name || capwords(typeToName(schemaType)),
    icon: schemaType as BenchlingIconName,
    submenu: hasSchemasDisabled
      ? null
      : _getTypeSubmenu(schemaType, types, schemasByAvailableType[schemaType], starredSchemaIds),
    value: getTypesValue(types),
    dataTestAttrs: {
      component: 'TypesAndSchemaMenu',
      element: schemaType,
    },
  };
}

function _getCollectibleTypeOption(schemasByAvailableType: {
  [type: string]: Array<TypesMenuSchema>;
}): OptionData | null {
  const allTypes = [
    ModelTypeConstants.TEMPLATE,
    ModelTypeConstants.SUBTEMPLATE,
    ModelTypeConstants.FORM_DEFINITION,
    ModelTypeConstants.RECIPE_PROCEDURE,
    ModelTypeConstants.ASSAY_PROCEDURE,
    ModelTypeConstants.REQUEST_V2_DEFINITION,
    ModelTypeConstants.ANALYSIS_TEMPLATE,
  ];
  // eslint-disable-next-line benchlint/no-in-operator
  const presentTypes = allTypes.filter((type) => type in schemasByAvailableType);

  if (presentTypes.length === 0) {
    return null;
  }

  return {
    name: 'Template',
    icon: 'template' as BenchlingIconName,
    submenu: [
      [
        {
          name: 'All',
          value: getTypesValue(presentTypes),
        },
      ],
      presentTypes.map((type) => _getCollectibleSubmenuItem(type)),
    ],
    value: getTypesValue(presentTypes),
    dataTestAttrs: {
      component: 'TypesAndSchemaMenu',
      element: 'collectible',
    },
  };
}

function _getCollectibleSubmenuItem(type: string): OptionData {
  return {
    name: capwords(typeToName(type)),
    value: getTypesValue([type]),
    dataTestAttrs: {
      component: 'TypesAndSchemaMenu',
      element: type,
    },
  };
}

function _getEntityTypeOption(
  allTypes: Array<string>,
  schemasByAvailableType: {[type: string]: Array<TypesMenuSchema>},
  filteredInterfacesByAvailableTypes: Array<SchemaInterfaceExtendingSchema>,
  starredSchemaIds: Array<string> | null
): OptionData | null {
  const types = allTypes.filter(
    (type) => schemasByAvailableType.hasOwnProperty(type) && isModelTypeEnabledForListing(type)
  );
  if (types.length === 0) {
    return null;
  }

  const hasSchemasDisabled = !isCapabilityEnabledForListing(Capability.REGISTRY);
  // eslint-disable-next-line no-restricted-syntax
  const allSchemas = _.flatten(types.map((type) => schemasByAvailableType[type] || []));
  return {
    name: 'Entity',
    icon: FolderItemIcon.BasicFolderItem,
    value: getTypesValue(types),
    submenu: hasSchemasDisabled
      ? getBioentityTypeOptionDatas(types)
      : [
          getSchemaSearchableMenuOptionDatas(
            [...allSchemas, ...filteredInterfacesByAvailableTypes],
            starredSchemaIds,
            {
              allSchemasValue: getTypesValue(types),
              sections: types.map((type) => ({
                title: capwords(typeToName(type)),
                value: isSearchableExclusiveSSIType(type)
                  ? getSearchExclusiveSSIInterface(type)
                  : getTypesValue([type]),
                containsSchema: (tagSchema: TagSchema) =>
                  getTagSchemaOligoAwareFolderItemType(tagSchema) === type,
              })),
              transformSchema: (tagSchema: TagSchema) => getTagSchemaValue(tagSchema),
              transformInterface: (schemaInterface) => getInterfaceValue(types, schemaInterface),
              includeArchivedSuffix: false,
            }
          ),
        ],
  };
}

/*
 * If only one process type is available, returns an option for that type and its available schemas.
 * If more than one process type is available, returns an option to filter by all task groups and all available task schemas.
 * In this case, the "all" option will return all task groups of all types.
 */
function _getProcessOptions(
  schemasByAvailableType: {
    [type: string]: Array<TypesMenuSchema>;
  },
  filteredInterfacesByAvailableTypes: Array<SchemaInterfaceExtendingSchema>,
  starredSchemaIds: Array<string> | null
): OptionData | null {
  const types = getAvailableProcessTypes(schemasByAvailableType);
  if (types.length === 0) {
    return null;
  } else if (types.length === 1) {
    return _getTypeOption(types[0], schemasByAvailableType, starredSchemaIds);
  }

  const allSchemas = types.map((type) => schemasByAvailableType[type] ?? []).flat();
  const schemaIdsToTypes: Record<string, string> = {};
  for (const [type, schemas] of Object.entries(schemasByAvailableType)) {
    for (const schema of schemas) {
      schemaIdsToTypes[schema.id] = type;
    }
  }

  return {
    name: 'Task group',
    icon: 'process' as BenchlingIconName,
    value: getTypesValue(types),
    submenu: [
      getSchemaSearchableMenuOptionDatas(allSchemas, starredSchemaIds, {
        allSchemasValue: getTypesValue(types),
        transformSchema: (schema) => getSchemaValue([schemaIdsToTypes[schema.id]], schema),
        unstarrable: PROCESS_TYPES.some((type) => !TYPES_WITH_STARRABLE_SCHEMAS.includes(type)),
        includeArchivedSuffix: false,
      }),
    ],
    dataTestAttrs: {
      component: 'TypesAndSchemaMenu',
      element: 'allProcesses',
    },
  };
}

function _getMenuOptionDatas(
  schemasByAvailableType: {[type: string]: Array<TypesMenuSchema>},
  filteredInterfacesByAvailableTypes: Array<SchemaInterfaceExtendingSchema> = [],
  starredSchemaIds: Array<string> | null,
  isTypeRequired: boolean = false
): SearchableMenuArchivableOptionDatas {
  return _.compact([
    !isTypeRequired && {
      name: 'Any type',
      value: getTypesValue(
        // eslint-disable-next-line no-restricted-syntax
        Object.keys(schemasByAvailableType)
          .filter((type) => isModelTypeEnabledForListing(type))
          .sort()
      ),
    },
    // Listing includes folders (both root folders and subfolders). Root folders are 1:1 to projects and
    // are surfaced only as projects in the UI, so show "Project or Folder" as the label.
    _getTypeOption(ModelTypeConstants.FOLDER, schemasByAvailableType, starredSchemaIds, {
      name: 'Project or Folder',
    }),
    _getTypeOption(ModelTypeConstants.STUDY, schemasByAvailableType, starredSchemaIds),
    _getEntityTypeOption(
      [
        ModelTypeConstants._DO_NOT_USE_ANTIBODY,
        ModelTypeConstants._DO_NOT_USE_OLIGO_CONJUGATE,
        ModelTypeConstants._DO_NOT_USE_OLIGO_DUPLEX,
        ModelTypeConstants.EQUIPMENT,
        ModelTypeConstants.PROTEIN,
        ModelTypeConstants.SEQUENCE,
        ModelTypeConstants.RNA_SEQUENCE,
        ModelTypeConstants.DNA_OLIGO,
        ModelTypeConstants.RNA_OLIGO,
        ModelTypeConstants.BASIC_FOLDER_ITEM,
        ModelTypeConstants.MIXTURE,
        ModelTypeConstants.MOLECULE,
      ],
      schemasByAvailableType,
      filteredInterfacesByAvailableTypes,
      starredSchemaIds
    ),
    // Inject special Reaction type if enabled.
    // TODO(BNCH-145194): remove this special logic when removing reaction search prototype
    ...(BenchlingConfig.CHEMISTRY_ENABLE_STOICHIOMETRY_TABLE
      ? [
          {
            name: 'Reaction',
            icon: 'reaction-vessel',
            submenu: null,
            value: getTypesValue([REACTION_PSEUDO_TYPE]),
          } as OptionData,
        ]
      : []),
    _getTypeOption(ModelTypeConstants.ENTRY, schemasByAvailableType, starredSchemaIds, {
      hasSchemasDisabled: !BenchlingConfig.ENABLE_ENTRY_SCHEMAS,
      extraTypes: schemasByAvailableType.hasOwnProperty(ModelTypeConstants.STAGE_ENTRY)
        ? [ModelTypeConstants.STAGE_ENTRY]
        : [],
    }),
    _getTypeOption(ModelTypeConstants.TEMPLATE_COLLECTION, schemasByAvailableType, starredSchemaIds),
    _getCollectibleTypeOption(schemasByAvailableType),
    _getProcessOptions(schemasByAvailableType, filteredInterfacesByAvailableTypes, starredSchemaIds),
    ...[
      // NOTE: It is okay to reorder these values, move them outside of this array, etc. as long as
      // the comments marking the code generation target are left in place.
      // --MENU OPTIONS TARGET FOR CODE GENERATION--
      ModelTypeConstants.REQUEST_V2_SUBMISSION,
      ModelTypeConstants.PROCESS_TASK,
      ModelTypeConstants.PROCESS_OUTPUT,
      ModelTypeConstants.ASSAY_RUN,
      ModelTypeConstants.BATCH,
      ModelTypeConstants.BULK_ASSEMBLY,
      ModelTypeConstants.DATASET,
      ModelTypeConstants.FILE,
      ModelTypeConstants.ASSAY_RUN_TASK,
      ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK,
      ModelTypeConstants.ASSAY_STEP_TASK,
      ModelTypeConstants.RECIPE_RUN_TASK,
      ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK,
      ModelTypeConstants.RECIPE_STEP_TASK,
      ModelTypeConstants.PROTOCOL,
      ModelTypeConstants.CONTAINER,
      ModelTypeConstants.PLATE,
      ModelTypeConstants.BOX,
      ModelTypeConstants.LOCATION,
      ModelTypeConstants.WORKFLOW,
      ModelTypeConstants.REQUEST,
      ModelTypeConstants.ANALYSIS,
      ModelTypeConstants.ANALYTICS_DASHBOARD,
      ModelTypeConstants.SEQUENCE_ANALYSIS,
      ModelTypeConstants.PROTEIN_ALIGNMENT,
      ModelTypeConstants.CONNECTION,
      ModelTypeConstants.WORKLIST,
      ModelTypeConstants.WORKSHEET,
      ModelTypeConstants.TEST_DEFINITION,
      ModelTypeConstants.TEST_ORDER,
      ModelTypeConstants.MODEL_RUN,
      // --END MENU OPTIONS TARGET FOR CODE GENERATION--
    ].map((modelType) => _getTypeOption(modelType, schemasByAvailableType, starredSchemaIds)),
  ]);
}

function _handleToggleSchemaStar(schemaId: string, onToggleStarFunctions: ToggleSchemaStarFunctions): void {
  const schemaType = APIUtil.apiIdToType(schemaId);
  const {onToggleTagSchemaStar, onToggleStorableSchemaStar} = onToggleStarFunctions;
  if (schemaType === ModelTypeConstants.TAG_SCHEMA) {
    onToggleTagSchemaStar(schemaId);
  } else if (APIUtil.isStorableSchema(schemaId)) {
    onToggleStorableSchemaStar(schemaId);
  }
}

export default function TypesAndSchemaMenu(props: TypesAndSchemaMenuProps): JSX.Element {
  const {onToggleTagSchemaStar, onToggleStorableSchemaStar} = props;
  const renderOptionContentBase = getRenderStarredOptionContent(
    (id: string) => {
      _handleToggleSchemaStar(id, {
        onToggleTagSchemaStar,
        onToggleStorableSchemaStar,
      });
    },
    props.starredSchemaIds,
    {getId: (optionData) => optionData.value.schemaId}
  );
  const onChangeValueBase = (option: TypesAndSchemaValue): void => {
    _handleSelect(
      option,
      props.filters,
      props.onChangeFilters,
      props.schemasByAvailableType,
      props.alwaysApplySchemasFilter
    );
  };

  const {onChangeValue, renderOptionContent, getMatchingOptionDatas, optionDatas} =
    useFilterArchivedDropdownOptions(
      _getMenuOptionDatas(
        props.schemasByAvailableType,
        props.filteredInterfacesByAvailableTypes,
        props.starredSchemaIds,
        props.isTypeRequired
      ),
      'Show archived schemas',
      onChangeValueBase,
      renderOptionContentBase
    );
  const typesFilter = FilterUtil.getFilterByKey(props.filters, FilterKey.TYPES);
  const schemaFilter = FilterUtil.getFilterByKey(props.filters, FilterKey.SCHEMA);
  return (
    <SearchableMenu
      getMatchingOptionDatas={getMatchingOptionDatas}
      onSelect={onChangeValue}
      optionDatas={optionDatas}
      renderOptionContent={renderOptionContent}
      selectedValue={{
        types: typesFilter ? typesFilter.value : [],
        schemaId: schemaFilter ? schemaFilter.value : null,
      }}
    />
  );
}
